import {Component, OnDestroy, OnInit} from '@angular/core';

import {locale as polish} from './i18n/pl';
import {locale as english} from './i18n/en';
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";
import {ComponentTitleService} from "../services/componentTitle.service";
import {ResourceService} from "../services/resource.service";
import {Resource} from "../models/resource";
import deviceTypesData from './../resource/resources/deviceTypes.json';
import {ResourceType} from "../resource/model/resourceType";
import {
    ResourceOnlineDetailsModalComponent
} from "./modal/resourceOnlineDetailsModal/resourceOnlineDetailsModal.component";
import {ResourceCalendarModalComponent} from "./modal/resourceCalendarModal/resourceCalendarModal.component";
import {UserWorkoutDetailsModalComponent} from "./modal/userWorkoutDetailsModal/userWorkoutDetailsModal.component";
import {DeviceStatsModalComponent} from "./modal/deviceStatsModal/deviceStatsModal.component";
import {MatDialog} from "@angular/material/dialog";
import {ReservationDataService} from "../services/reservationData.service";
import {ReservationData} from "../models/reservationData";
import {DatePipe} from "@angular/common";
import {first} from "rxjs/operators";
import {TrainingListModalComponent} from "../training/trainingListModal.component";

@Component({
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss',]
})
export class DashboardComponent implements OnDestroy, OnInit {
    title = 'app-dashboard';
    view: string = 'preview';

    resources: Resource[] = [];
    allResources: Resource[] = [];
    deviceTypes: ResourceType[] = deviceTypesData;
    selectedDeviceType: string | null = null;
    selectedStatus: string | null = null;
    serialNumberFilter: string = '';
    activeReservations: ReservationData[] = [];
    showActiveWorkouts: boolean = false;

    constructor(
        private translationLoader: FuseTranslationLoaderService,
        private componentTitleService: ComponentTitleService,
        private resourceService: ResourceService,
        public dialog: MatDialog,
        private reservationDataService: ReservationDataService,
        private datePipe: DatePipe
    ) {
        this.translationLoader.loadTranslations(polish, english);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Dashboard');

        let ids = [];

        this.resourceService.getAll().subscribe((list: Resource[]) => {
            this.allResources = [];

            for (let i = 0; i < list.length; i++) {
                if (!list[i].type) {
                    continue;
                }

                for (let j = 0; j < this.deviceTypes.length; j++) {
                    if (this.deviceTypes[j].id !== list[i].type) {
                        continue;
                    }

                    list[i].image = this.deviceTypes[j].image;
                    break;
                }

                if (list[i].serviceDeviceId) {
                    ids.push(list[i].id);
                    this.allResources.push(list[i]);
                }
            }

            this.resourceService.getManyServiceDeviceById(ids).subscribe(datas => {
                for (let i = 0; i < datas.length; i++) {
                    for (let j = 0; j < this.allResources.length; j++) {
                        if (this.allResources[j].serialNumber != datas[i].serialNumber) {
                            continue;
                        }
                        if (datas[i].heartbeatAt) {
                            let heartbeatAt = new Date(datas[i].heartbeatAt);
                            let currentDate = new Date();
                            let c = currentDate.getTime() - (5 * 60000); //-2 minut

                            if (c < heartbeatAt.getTime()) {
                                this.allResources[j].online = true;
                            }
                        }

                        this.allResources[j].workout = false;

                        if (datas[i].workout) {
                            this.allResources[j].workout = true;
                        }

                        if (!datas[i].groupingDevice) {
                            this.allResources[j].groupingDevice = false;
                        } else {
                            this.allResources[j].groupingDevice = datas[i].groupingDevice;
                        }

                        this.allResources[j].currentSpeedLevel = datas[i].currentSpeedLevel;
                        this.allResources[j].currentInfraLevel = datas[i].currentInfraLevel;
                        this.allResources[j].currentCollagenFlag = datas[i].currentCollagenFlag;
                        this.allResources[j].currentCalories = datas[i].currentCalories;
                        this.allResources[j].currentDistance = datas[i].currentDistance;
                        this.allResources[j].currentLymphFlag = datas[i].currentLymphFlag;
                        this.allResources[j].currentCardNumber = datas[i].currentCardNumber;
                        this.allResources[j].currentPulse = datas[i].currentPulse;
                        this.allResources[j].currentSpeed = datas[i].currentSpeed;
                        this.allResources[j].user = datas[i].user;

                        if (this.allResources[j].currentSpeed > 0) {
                            this.allResources[j].currentSpeed = this.allResources[j].currentSpeed/10;
                        }

                        if (this.allResources[j].currentDistance > 0) {
                            this.allResources[j].currentDistance = this.allResources[j].currentDistance/100;
                        }

                        this.allResources[j].currentSteps = datas[i].currentSteps;
                        this.allResources[j].currentVacuLevel = datas[i].currentVacuLevel;
                        this.allResources[j].timeLeft = datas[i].timeLeft;

                        // Debug: log timeLeft value
                        console.log('Device:', this.allResources[j].name, 'timeLeft:', datas[i].timeLeft);
                    }
                }

                this.applyFilters();
                this.loadActiveReservations();
            });
        }, (error) => {

        });
    }

    ngOnInit() {

    }

    loadActiveReservations(): void {
        const now = new Date();
        const todayStart = this.datePipe.transform(now, 'yyyy-MM-dd') + ' 00:00:00';
        const todayEnd = this.datePipe.transform(now, 'yyyy-MM-dd') + ' 23:59:59';

        this.reservationDataService.getByDate(todayStart, todayEnd)
            .pipe(first())
            .subscribe((reservations: ReservationData[]) => {
                const currentTime = now.getTime();

                // Filter only current active reservations
                this.activeReservations = reservations.filter(reservation => {
                    if (reservation.completed) {
                        return false;
                    }

                    // Apply time correction like in calendar component
                    let startHoursDiff = -1;
                    let endHoursDiff = -1;
                    let dateStart = new Date(reservation.startDate);
                    let dateEnd = new Date(reservation.endDate);

                    // Check if date is in DST (between last Sunday of March and last Sunday of October)
                    if (dateStart >= this.lastSunday(dateStart.getFullYear(), 3) &&
                        dateStart < this.lastSunday(dateStart.getFullYear(), 10)) {
                        startHoursDiff = -2;
                    }

                    if (dateEnd >= this.lastSunday(dateEnd.getFullYear(), 3) &&
                        dateEnd < this.lastSunday(dateEnd.getFullYear(), 10)) {
                        endHoursDiff = -2;
                    }

                    // Apply correction to times
                    const startTime = new Date(dateStart.getTime() + (startHoursDiff * 3600000)).getTime();
                    const endTime = new Date(dateEnd.getTime() + (endHoursDiff * 3600000)).getTime();

                    return startTime <= currentTime && currentTime <= endTime;
                });
            }, (error) => {
                console.error('Error loading active reservations:', error);
            });
    }

    lastSunday(year: number, month: number): Date {
        const date = new Date(year, month, 1, 12);
        const weekday = date.getDay();
        const dayDiff = weekday === 0 ? 7 : weekday;
        date.setDate(date.getDate() - dayDiff);
        return date;
    }

    openResourceDetails(resource: Resource): void {
        const dialogRef = this.dialog.open(ResourceOnlineDetailsModalComponent, {
            width: '4000px',
            data: {
                resource: resource
            },
            position: {
                top: '10px',
            }
        });

        dialogRef.afterClosed().subscribe(result => {

        });
    }

    openResourceCalendar(resource: Resource): void {
        const now = new Date();
        const todayStart = this.datePipe.transform(now, 'yyyy-MM-dd') + ' 00:00:00';
        const todayEnd = this.datePipe.transform(now, 'yyyy-MM-dd') + ' 23:59:59';

        this.reservationDataService.getByDate(todayStart, todayEnd)
            .pipe(first())
            .subscribe((reservations: ReservationData[]) => {
                const currentTime = now.getTime();

                // Filter reservations for this device from current time onwards
                const deviceReservations = reservations.filter(reservation => {
                    // Check if this reservation includes the device
                    const hasDevice = reservation.reservedResources &&
                        reservation.reservedResources.some(r => r.id === resource.id);

                    if (!hasDevice) {
                        return false;
                    }

                    // Apply DST correction
                    let startHoursDiff = -1;
                    let dateStart = new Date(reservation.startDate);

                    if (dateStart >= this.lastSunday(dateStart.getFullYear(), 3) &&
                        dateStart < this.lastSunday(dateStart.getFullYear(), 10)) {
                        startHoursDiff = -2;
                    }

                    const startTime = new Date(dateStart.getTime() + (startHoursDiff * 3600000)).getTime();

                    // Include reservations that start from now onwards (not completed)
                    return startTime >= currentTime && !reservation.completed;
                });

                const dialogRef = this.dialog.open(ResourceCalendarModalComponent, {
                    width: '600px',
                    panelClass: 'resource-calendar-modal-component',
                    data: {
                        resource: resource,
                        reservations: deviceReservations
                    },
                    position: {
                        top: '10px',
                    }
                });

                dialogRef.afterClosed().subscribe(result => {

                });
            }, (error) => {
                console.error('Error loading device reservations:', error);
            });
    }

    showActiveWorkoutsTab(): void {
        this.showActiveWorkouts = true;
        this.selectedDeviceType = null;
        this.selectedStatus = null;
        this.serialNumberFilter = '';
    }

    filterByDeviceType(type: string | null): void {
        this.showActiveWorkouts = false;
        this.selectedDeviceType = type;
        this.selectedStatus = null;
        this.applyFilters();
    }

    getActiveWorkoutsCount(): number {
        const activeDevices = this.allResources.filter(r => r.workout && !r.groupingDevice).length;
        return activeDevices + this.activeReservations.length;
    }

    getResourceForReservation(reservation: ReservationData): Resource | null {
        if (!reservation.reservedResources || reservation.reservedResources.length === 0) {
            return null;
        }

        const resourceId = reservation.reservedResources[0].id;
        return this.allResources.find(r => r.id === resourceId) || null;
    }

    getClientForReservation(reservation: ReservationData): any | null {
        if (!reservation.customer) {
            return null;
        }

        return reservation.customer;
    }

    getEmployeeForReservation(reservation: ReservationData): any | null {
        if (!reservation.reservedUsers || reservation.reservedUsers.length === 0) {
            return null;
        }

        return reservation.reservedUsers[0];
    }

    getActiveDevices(): Resource[] {
        return this.allResources.filter(r => r.workout && !r.groupingDevice);
    }

    filterByStatus(status: string | null): void {
        this.selectedStatus = status;
        this.applyFilters();
    }

    filterBySerialNumber(event: any): void {
        this.serialNumberFilter = event.target.value.toLowerCase();
        this.applyFilters();
    }

    applyFilters(): void {
        this.resources = this.allResources.filter(resource => {
            // Filter by device type
            if (this.selectedDeviceType && resource.type !== this.selectedDeviceType) {
                return false;
            }

            // Filter by status
            if (this.selectedStatus === 'online' && !resource.online) {
                return false;
            }
            if (this.selectedStatus === 'offline' && (resource.online || resource.groupingDevice)) {
                return false;
            }
            if (this.selectedStatus === 'training' && !resource.workout) {
                return false;
            }
            if (this.selectedStatus === 'free' && (resource.workout || resource.groupingDevice)) {
                return false;
            }

            // Filter by serial number
            if (this.serialNumberFilter && resource.serialNumber) {
                if (!resource.serialNumber.toLowerCase().includes(this.serialNumberFilter)) {
                    return false;
                }
            }

            return true;
        });
    }

    getDeviceTypeCount(type: string | null): number {
        if (!type) {
            return this.allResources.length;
        }
        return this.allResources.filter(r => r.type === type).length;
    }

    openUserWorkoutDetails(resource: Resource): void {
        if (!resource.user) {
            return;
        }

        const dialogRef = this.dialog.open(UserWorkoutDetailsModalComponent, {
            width: '500px',
            data: {
                user: resource.user,
                deviceName: resource.name + (resource.serialNumber ? ' (' + resource.serialNumber + ')' : '')
            },
            position: {
                top: '40px',
            },
            panelClass: 'custom-dialog-container'
        });

        dialogRef.afterClosed().subscribe(result => {

        });
    }

    openActiveTrainingsModal(): void {
        const dialogRef = this.dialog.open(TrainingListModalComponent, {
            width: '90vw',
            maxWidth: '1400px',
            height: '80vh',
            panelClass: 'training-list-modal',
            data: {
                isActive: true,
                title: 'Active Trainings'
            }
        });
    }

    openDeviceStats(resource: Resource): void {
        const dialogRef = this.dialog.open(DeviceStatsModalComponent, {
            width: '900px',
            maxWidth: '95vw',
            data: {
                resource: resource
            },
            position: {
                top: '10px',
            }
        });

        dialogRef.afterClosed().subscribe(result => {

        });
    }

    formatTimeLeft(seconds: number): string {
        if (!seconds || seconds <= 0) return '0:00';

        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;

        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }

    ngOnDestroy(): void {

    }
}
